import {z} from 'zod';
import {timelineActionClientSchema} from '../timeline/action';
import {objectIdSchema} from '../shared';

export type IX3ActionPresetVersionedResource = {
  id: ActionPresetId;
  version: number;
};

export const actionPresetIdSchema = z.string();
export type ActionPresetId = z.infer<typeof actionPresetIdSchema>;

export const actionPresetGroupIdSchema = z.string();
export type ActionPresetGroupId = z.infer<typeof actionPresetGroupIdSchema>;

/** Groups of action presets can be defined using a shared groupId */
export const actionPresetGroupSchema = z.object({
  /** Unique identifier for the group */
  id: z.string(),
  /** Display name of the group */
  name: z.string(),
});
export type ActionPresetGroup = z.infer<typeof actionPresetGroupSchema>;

/** An action preset is based on a timeline action.
 * Unlike regular timeline actions, presets:
 * - Have predefined IDs (not auto-generated)
 * - Do not include `targets`
 */
export const actionPresetActionSchema = timelineActionClientSchema.omit({
  id: true,
  targets: true,
});
export type ActionPresetAction = z.infer<typeof actionPresetActionSchema>;

/** ActionPreset format used on client and server side. */
export const actionPresetSchema = z.object({
  /**
   * Defines the type of action preset
   * - 'swap-class': Toggles CSS classes on elements
   * - 'custom': User-defined custom actions
   * - 'run-script': Executes custom JavaScript
   * - 'built-in': System-provided preset actions
   */
  type: z.enum(['swap-class', 'custom', 'run-script', 'built-in']),
  /** Unique identifier for the preset */
  id: actionPresetIdSchema,
  /** Display name of the preset */
  name: z.string(),
  /** Whether this preset has been deleted */
  deleted: z.boolean().default(false),
  /** Detailed description of what the preset does */
  description: z.string(),
  /** Optional reference to a group this preset belongs to. If not set, preset is ungrouped */
  groupId: actionPresetGroupIdSchema.optional(),
  /** Timeline action configuration for this preset (without targets and id) */
  action: actionPresetActionSchema,
  /** version used for realtime */
  version: z.number().int().nonnegative().optional(),
});
export type ActionPreset = z.infer<typeof actionPresetSchema>;

/** Array schema for utilizing zod's validating capabilities during sync requests.
 * Each element in UpdateActionPresetParams is a full ActionPreset */
export const actionPresetsSchema = z.array(actionPresetSchema);
export type ActionPresets = z.infer<typeof actionPresetsSchema>;

/** Represents the full actionPreset document in Mongo, including metadata.
 * This is returned to the client on read/write operations. */
export const rawActionPresetSchema = actionPresetSchema.extend({
  createdOn: z.union([z.date(), z.null()]).optional(),
  updatedOn: z.union([z.date(), z.null()]).optional(),
  siteId: objectIdSchema,
});
export type RawActionPreset = z.infer<typeof rawActionPresetSchema>;

/**
 * Utilities for converting between different ActionPreset formats
 */
export const actionPresetTransforms = {
  rawActionPresetToClient: (rawActionPreset: RawActionPreset): ActionPreset => {
    return {
      type: rawActionPreset.type,
      id: rawActionPreset.id,
      name: rawActionPreset.name,
      description: rawActionPreset.description,
      action: rawActionPreset.action,
      deleted: rawActionPreset.deleted,
      version: rawActionPreset.version,
    };
  },

  rawActionPresetToVersionedResource: (
    rawActionPreset: RawActionPreset
  ): IX3ActionPresetVersionedResource => {
    return {
      id: rawActionPreset.id,
      // Default to 0 (indicating no version exists in the db yet) if the version is missing
      version: rawActionPreset.version || 0,
    };
  },

  /**
   * Converts a client actionPreset to a raw actionPreset object. Note: since the client
   * ActionPreset doesn't contain created/updated dates, we drop them from the Raw actionPreset as well. This is safer
   * and more accurate than creating placeholder Date objects with the current timestamp.
   */
  clientToRawActionPreset: (
    clientActionPreset: ActionPreset,
    siteId: string
  ): RawActionPreset => {
    return {
      ...clientActionPreset,
      siteId,
    };
  },
};
